import { EventInput } from '@fullcalendar/core';
import { Api } from '@monorepo/shared/apiClient';
import { addMilliseconds, format } from 'date-fns';
import {
  ComplianceCalendarEvent,
  ComplianceCalendarEventStatus,
  localEquivalentOfUTC,
  UTCEquivalentOfLocal,
} from 'mapistry-shared';
import React, { MouseEvent } from 'react';
import styled from 'styled-components';

export enum CalendarViewType {
  Month = 'dayGridMonth', // FullCalendar view name
  Week = 'week',
  List = 'list',
}

const CALENDAR_EVENT_STATUSES_THAT_COUNT_AS_COMPLETED =
  new Set<ComplianceCalendarEventStatus>([
    ComplianceCalendarEventStatus.COMPLETE,
    ComplianceCalendarEventStatus.EXCEPTION,
  ]);

export function resolveCalendarViewType(view: string): CalendarViewType {
  if ((Object.values(CalendarViewType) as string[]).includes(view)) {
    return view as CalendarViewType;
  }
  return CalendarViewType.Month;
}

export const isEventClickable = (status: ComplianceCalendarEventStatus) =>
  status !== ComplianceCalendarEventStatus.FUTURE_INACTIVE;

type ComplianceCalendarBackendEvent = Awaited<
  ReturnType<typeof Api.fetchComplianceCalendarEvents>
>[number] & { isLoadingIndicator?: never };

type LoadingIndicatorEvent = {
  isLoadingIndicator: true;
  dueDate: ComplianceCalendarBackendEvent['dueDate'];
};

export function convertEventToFullCalendarFormat(
  event: ComplianceCalendarBackendEvent | LoadingIndicatorEvent,
): EventInput {
  if (event.isLoadingIndicator) {
    return {
      start: event.dueDate,
      interactive: false,
      isLoadingIndicator: true,
      className: 'loading-indicator',
    };
  }

  return {
    id: event.id,
    start: event.dueDate,
    status: event.status,
    title: event.title,
    type: event.type,
    // not FullCalendar's top-level `url`, so it doesn't render its own focusable link around ours

    ...(isEventClickable(event.status)
      ? { extendedProps: { url: event.url } }
      : { className: 'is-not-clickable', interactive: false }),
  };
}

/** Link props for calendar events; unclickable events render as a plain div. */
export const getEventLinkProps = (
  event: Pick<ComplianceCalendarEvent, 'status' | 'url'>,
  navigateToEventUrl: (url: string) => void,
) =>
  event.url
    ? {
        href: event.url,
        onClick: (clickEvent: MouseEvent) => {
          clickEvent.preventDefault();
          if (event.url) {
            navigateToEventUrl(event.url);
          }
        },
      }
    : { as: 'div' as const };

export const getDaysBetween = (start: Date, exclusiveEnd: Date) => {
  const days: Date[] = [];
  for (
    let day = new Date(start);
    day < exclusiveEnd;
    day.setUTCDate(day.getUTCDate() + 1)
  ) {
    days.push(new Date(day));
  }
  return days;
};

export const getEventDayKey = (
  dueDate: ComplianceCalendarEvent['dueDate'] | Date,
) => format(localEquivalentOfUTC(new Date(dueDate)), 'yyyy-MM-dd');

/** The Compliance Calendar functions in UTC. Comparisons against "now" thus also have to happen in UTC-now */
export const getComplianceCalendarNow = () => UTCEquivalentOfLocal(new Date());

export const isTodayUtcDay = (day: Date) =>
  getEventDayKey(day) === getEventDayKey(getComplianceCalendarNow());

export const isPastUtcDay = (day: Date) =>
  getEventDayKey(day) < getEventDayKey(getComplianceCalendarNow());

export const groupEventsByDay = (events: ComplianceCalendarEvent[]) => {
  const eventsByDay = new Map<string, ComplianceCalendarEvent[]>();

  events.forEach((event) => {
    const dayKey = getEventDayKey(event.dueDate);
    const dayEvents = eventsByDay.get(dayKey) ?? [];
    dayEvents.push(event);
    eventsByDay.set(dayKey, dayEvents);
  });

  return eventsByDay;
};

export type DayWithEvents = {
  day: Date;
  dayKey: string;
  events: ComplianceCalendarEvent[];
};

export const getDaysWithEvents = ({
  events,
  exclusiveEnd,
  rangeStart,
}: {
  events: ComplianceCalendarEvent[];
  exclusiveEnd: Date;
  rangeStart: Date;
}): DayWithEvents[] => {
  const eventsByDay = groupEventsByDay(events);

  return getDaysBetween(rangeStart, exclusiveEnd).map((day) => {
    const dayKey = getEventDayKey(day);
    return {
      day,
      dayKey,
      events: eventsByDay.get(dayKey) ?? [],
    };
  });
};

export const exclusiveToInclusiveRange = (
  start: Date,
  end: Date,
  view?: CalendarViewType,
) => {
  const rangeStart = new Date(start);
  const inclusiveEnd = new Date(end);
  inclusiveEnd.setUTCDate(inclusiveEnd.getUTCDate() - 1);

  // ±1 week or month so next/prev already has data
  if (view === CalendarViewType.Week) {
    rangeStart.setUTCDate(rangeStart.getUTCDate() - 7);
    inclusiveEnd.setUTCDate(inclusiveEnd.getUTCDate() + 7);
  } else if (view === CalendarViewType.Month) {
    rangeStart.setUTCMonth(rangeStart.getUTCMonth() - 1, 1);

    // day 0 of month+2 = last day of next month
    inclusiveEnd.setUTCMonth(inclusiveEnd.getUTCMonth() + 2, 0);
  }

  return {
    startDateString: format(localEquivalentOfUTC(rangeStart), 'yyyy-MM-dd'),
    endDateString: format(localEquivalentOfUTC(inclusiveEnd), 'yyyy-MM-dd'),
  };
};

/** True when [visibleStart, visibleExclusiveEnd) falls inside an inclusive yyyy-MM-dd fetch window. */
export function isVisibleRangeInFetchWindow({
  visibleStart,
  visibleExclusiveEnd,
  fetchWindowStart,
  fetchWindowEnd,
}: {
  visibleStart: Date;
  visibleExclusiveEnd: Date;
  fetchWindowStart: string;
  fetchWindowEnd: string;
}): boolean {
  const visibleStartStr = format(
    localEquivalentOfUTC(visibleStart),
    'yyyy-MM-dd',
  );
  const inclusiveEnd = addMilliseconds(visibleExclusiveEnd, -1);
  const visibleEndStr = format(
    localEquivalentOfUTC(inclusiveEnd),
    'yyyy-MM-dd',
  );

  return visibleStartStr >= fetchWindowStart && visibleEndStr <= fetchWindowEnd;
}

const getLoadingIndicatorEvent = (date: Date): LoadingIndicatorEvent => ({
  dueDate: date.toISOString(),
  isLoadingIndicator: true,
});

export const createLoadingEvents = (start: Date, end: Date) => {
  const loadingEvents: LoadingIndicatorEvent[] = [];
  const daysInBetween = getDaysBetween(start, end);

  daysInBetween.forEach((day) => {
    const loadingEvent = getLoadingIndicatorEvent(day);
    // show three bars in every day
    loadingEvents.push(loadingEvent, loadingEvent, loadingEvent);
  });

  return loadingEvents;
};

export function filterCalendarEvents(
  events: ComplianceCalendarEvent[],
  filters: Api.ComplianceCalendarFilters,
): ComplianceCalendarEvent[] {
  return events.filter((event) => {
    // include the types given, filtering out what's missing
    if (!filters.eventTypes.includes(event.type)) {
      return false;
    }

    if (
      filters.hideCompleted &&
      CALENDAR_EVENT_STATUSES_THAT_COUNT_AS_COMPLETED.has(event.status)
    ) {
      return false;
    }

    // events without assignees stay visible when filtering by user
    if (filters.userIds.length > 0 && event.assigneeIds) {
      return event.assigneeIds.some((id) => filters.userIds.includes(id));
    }

    return true;
  });
}

const isCompletedEvent = (event: ComplianceCalendarEvent) =>
  CALENDAR_EVENT_STATUSES_THAT_COUNT_AS_COMPLETED.has(event.status);

export const areAllDayEventsCompleted = (events: ComplianceCalendarEvent[]) =>
  events.length > 0 && events.every(isCompletedEvent);

export const areAllDayEventsExceptions = (events: ComplianceCalendarEvent[]) =>
  events.length > 0 &&
  events.every(
    (event) => event.status === ComplianceCalendarEventStatus.EXCEPTION,
  );

const StyledDiv = styled.div`
  padding-right: 0.5rem;
`;

export const getDaySummaryText = (events: ComplianceCalendarEvent[]) => {
  if (events.length === 0) {
    return 'No items due';
  }

  const completedCount = events.filter(isCompletedEvent).length;

  return (
    <StyledDiv>
      <b>
        {completedCount}/{events.length}
      </b>
      &nbsp;items completed
    </StyledDiv>
  );
};
